import { useEffect, useState } from 'react';
import { getDocs } from 'firebase/firestore';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFirebaseService } from '../../../data/FirebaseService';
import { Delete } from '@mui/icons-material';
import ConfirmationDialog from '../../../ui/ConfirmationDialog';
import { toast } from 'react-toastify';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseConfig } from '../../../data/firebaseConfig';
import { AddGroupDialog } from './AddGroupDialog';

export interface Group {
    id: string;
    name: string;
}

const GroupsPage: React.FC = () => {
    const [groups, setGroups] = useState<any[]>([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [currentRow, setCurrentRow] = useState<Group | null>(null);
    const firebaseService = useFirebaseService();
    const navigate = useNavigate();

    const functions = getFunctions(FirebaseConfig.app);
    const deleteGroup = httpsCallable(functions, "deleteGroup");

    useEffect(() => {
        if (firebaseService) {
            const fetchGroups = async () => {
                const groupsCollection = firebaseService.getCollection('groups');
                const groupsSnapshot = await getDocs(groupsCollection);
                const groupsList = groupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setGroups(groupsList);
            };

            fetchGroups();
        }
    }, [firebaseService]);

    const columns: GridColDef[] = [
        { field: 'name', headerName: 'Group Name', width: 300 },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            getActions: (params) => {
                return [
                    (<GridActionsCellItem
                        label="Delete"
                        onClick={() => handleDelete(params.row)}
                        showInMenu={false}
                        icon={<Delete />}
                    />)
                ];
            }
        }
    ];

    const handleDelete = (row: Group) => {
        setCurrentRow(row);
        setOpenDeleteDialog(true);
    }

    const handleConfirmDelete = async () => {
        if (currentRow) {
            try {
                await deleteGroup({ groupId: currentRow.id });
                setGroups(groups.filter(group => group.id !== currentRow.id));
            }
            catch (e) {
                console.error(e);
                toast.error('Failed to remove group. Please try again.', { position: 'bottom-right' });
            }
        }
        // Add your disable logic here
        setOpenDeleteDialog(false); // Close the dialog after confirming
    };

    const handleRowClick = (params: GridRowParams) => {
        navigateToGroup(params.row.id);
    };

    const navigateToGroup = (groupId: string) => {
        navigate(`/audience/groups/${groupId}`);
    }

    const handleGroupAdded = (group: Group) => {
        setGroups([...groups, group]);
        navigateToGroup(group.id);
    };

    return (
        <div>
            <Typography variant="h5" component="h2" className='page-title'>Groups</Typography>
            <AddGroupDialog onGroupAdded={handleGroupAdded} />
            <div style={{ height: 'calc(100vh - 220px)', width: '100%' }}>
                <DataGrid rows={groups} columns={columns} onRowClick={handleRowClick} />
            </div>
            <ConfirmationDialog open={openDeleteDialog}
                title='Confirm Delete'
                content='Are you sure you want to remove this group?'
                onConfirm={handleConfirmDelete}
                onCancel={() => setOpenDeleteDialog(false)} />
        </div>
    );
};

export default GroupsPage;
